import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import './module.css';
import '../backlog/backlog.css';
import { useModuleList, fetchModuleRows } from './queries';
import { ColumnFilter } from './ColumnFilter';
import { QueryCombo } from './QueryCombo';
import { QueryDatePicker } from './QueryDatePicker';
import { DateRangeSelect, TRACKING_RANGES } from './DateRangeSelect';
import { MODULE_CRUD, type CrudField } from './moduleCrud';
import { runLoading, LoaderSymbol, BusyIcon } from '../../components/loading';
import { SettingsDetailPage } from './SettingsDetailPage';
import { ModuleCrudDialog } from './ModuleCrudDialog';
import { MsrDetailDialog } from './MsrDetailDialog';
import { SampleFieldConfigDetail } from './SampleFieldConfigDetail';
import { MsrDataImportConfigDialog } from './MsrDataImportConfigDialog';
import { MsrSummaryDialog } from './MsrSummaryDialog';
import { MsrAutoGenerateDialog } from './MsrAutoGenerateDialog';
import { RelatedLookups } from './RelatedLookups';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { saveBlobAs } from '../../utils/exportData';
import { api, runWithAuditReason } from '../../api/client';
import { fuzzyFilter } from '../../utils/fuzzyMatch';
import { boClassOf } from '../../layouts/boClass';
import { useReasonGate, useConfirm } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
// Global grid features (Sort · …) — the SAME header right-click menu AppDataGrid
// uses, wired here via a tiny adapter over this grid's own sortCol/sortDesc state
// (client 2026-08-03: "all the grids, list AND detail view — global requirement").
import { useHeaderMenu, useColumnManager, loadGridPrefs, saveGridPrefs, type HeaderMenuSort } from '../../components/AppDataGrid';
import { StepHint } from '../../components/StepHint/StepHint';
import { ViewToggle } from '../../components/ViewToggle';
import { useNotify } from '../../components/notifications';
import { useNavActions } from '../../hooks/useNavActions';
import { fmtLocalDateTime } from '../../utils/datetime';
import {
  PAGE_SIZES, DEFAULT_PAGE_SIZE, pageSizeLabel, MODULE_DEFAULT_RANGE_DAYS, wirePageSize,
} from '../../components/listDefaults';

const RANGES: { label: string; days: number | null }[] = [
  { label: '1M', days: 30 }, { label: '3M', days: 90 }, { label: '6M', days: 180 },
  { label: '1Y', days: 365 }, { label: '2Y', days: 730 }, { label: '5Y', days: 1825 },
  { label: '10Y', days: 3650 }, { label: 'All', days: null },
];
// PAGE_SIZES + pageSizeLabel sourced from shared listDefaults.ts.
const ISO = /^\d{4}-\d{2}-\d{2}T/;

// UI-8: ISO/date string → yyyy-MM-dd for an inline <input type="date">.
function toDateInputLocal(raw: unknown): string {
  if (raw == null || raw === '') return '';
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// Minimal CSV → array-of-objects parser for the bulk Import (handles quoted
// fields + escaped "" quotes).  Keys are the header-row labels (which match the
// Template download), so the server binds them via [JsonPropertyName].
function parseCsv(text: string): Record<string, string>[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.trim() !== '');
  if (lines.length < 2) return [];
  const parseLine = (line: string): string[] => {
    const out: string[] = []; let cur = ''; let q = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (q) {
        if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; }
        else cur += c;
      } else if (c === ',') { out.push(cur); cur = ''; }
      else if (c === '"') { q = true; }
      else cur += c;
    }
    out.push(cur);
    return out;
  };
  const headers = parseLine(lines[0]!).map((h) => h.trim());
  return lines.slice(1).map((l) => {
    const cells = parseLine(l);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] ?? '').trim(); });
    return obj;
  });
}

// Modules that get the full "Query Samples" dialog + Query button.
// Result Samples + Historic Results + Monthly Summary Repository (each shows
// only its own grid columns — see QUERY_FIELDS_BY_MODULE / QUERY_DATES_BY_MODULE).
const ADVANCED_FILTER_MODULES = new Set(['dc2-resultsamples', 'histres', 'msr']);

// Modules that get the enhanced toolbar: custom orange Date Range dropdown,
// 3M default, Retrieve-gated range (no auto-fetch), and colored Retrieve/Export
// buttons. Superset of ADVANCED_FILTER_MODULES.
const ENHANCED_TOOLBAR_MODULES = new Set(['dc2-resultsamples', 'histres']);

// CRUD modules whose New/Edit opens a BESPOKE overlay dialog instead of the
// generic full-page SettingsDetailPage — their detail can't be expressed as a
// MODULE_CRUD field list.  'msr-data-import-config' = the five multi-select GUID
// pickers + the ImportMode radio.  Kept in one place so the early-return guard
// and the overlay render block stay in sync.
// NOTE: 'msr' (Monthly Summary Repository) was moved OUT of this set (2026-07-09)
// — it now opens a FULL-PAGE inline detail (List/Detail tab, Settings style) via
// <MsrDetailDialog inline> in the full-page branch below, not a popup.
const BESPOKE_DETAIL_MODULES = new Set(['msr-data-import-config']);

// CRUD modules whose full-page detail is rendered by a BESPOKE inline component
// (still List/Detail tab + full-page, just not the generic SettingsDetailPage).

// "Query Samples" dialog — the text fields (ALPACA layout), each mapped to a
// Result Samples column. Only fields that actually filter this grid are shown.
const QUERY_FIELDS: { key: string; label: string }[] = [
  { key: 'projectId', label: 'Project ID' },
  { key: 'siteName',  label: 'Site' },
  { key: 'client',    label: 'Client' },
  { key: 'jobId',     label: 'Job ID' },
  { key: 'testName',  label: 'Test' },
  { key: 'test',      label: 'Method' },
  { key: 'parameter', label: 'Parameter' },
];

// Date fields in the "Query Samples" dialog — each a From/To pair backed by
// its own server params. Received reuses the toolbar's fromDate/toDate.
const QUERY_DATES = [
  { key: 'collected', label: 'Date Collected' },
  { key: 'received',  label: 'Date Received' },
  { key: 'analyzed',  label: 'Date Analyzed' },
];

// Per-module Query Panel layout — each entry lists ONLY the combos/date ranges
// that exist as columns on that module's grid (a combo's `key` must be a real
// column so its distinct list resolves).  Modules not listed here fall back to
// the full Result Samples layout (QUERY_FIELDS / QUERY_DATES).
const QUERY_FIELDS_BY_MODULE: Record<string, { key: string; label: string }[]> = {
  'dc2-resultsamples': QUERY_FIELDS,
  // Historic Results — its grid has projectId / siteName / client / jobId /
  // method / parameter (no separate Test column; Method is keyed 'method').
  'histres': [
    { key: 'projectId', label: 'Project ID' },
    { key: 'siteName',  label: 'Site' },
    { key: 'client',    label: 'Client' },
    { key: 'jobId',     label: 'Job ID' },
    { key: 'method',    label: 'Method' },
    { key: 'parameter', label: 'Parameter' },
  ],
  // Monthly Summary Repository — match GoLive's Query dialog exactly: Year, Month, MSRID.
  'msr': [
    { key: 'year',  label: 'Year' },
    { key: 'month', label: 'Month' },
    { key: 'msrId', label: 'MSRID' },
  ],
};
const QUERY_DATES_BY_MODULE: Record<string, { key: string; label: string }[]> = {
  'dc2-resultsamples': QUERY_DATES,
  // Historic Results grid shows Date Received + Analysis Date (no Date Collected).
  'histres': [
    { key: 'received',  label: 'Date Received' },
    { key: 'analyzed',  label: 'Date Analyzed' },
  ],
  // MSR — no Query-panel date pickers; the toolbar Date Range covers CreatedDate.
  'msr': [],
};

// Forms rendered as a collapsible grouped grid (GoLive parity).  Maps the
// module key → { col: column to group by, prefix: header label prefix }.
// The grouped column becomes the "{prefix}{value} (Count=N)" header; the
// remaining columns show as detail rows.
const GROUPED_BY: Record<string, { col: string; prefix: string }> = {
  'dc2-userroleright':  { col: 'role',     prefix: 'Role: ' },
  'dc2-userpermission': { col: 'groupKey', prefix: '' },
};

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '';
  // Dates render in the viewer's LOCAL timezone, 24-hour, no AM/PM (CLAUDE.md
  // §14b.6).  fmtLocal force-UTCs the wire string; fall back to raw if unparseable.
  if (typeof v === 'string' && ISO.test(v)) return fmtLocalDateTime(v) || v;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

// Columns whose value domain is a TEXT enum that happens to include 'Yes'/'No'
// (e.g. Data Package Review Checklist's Default = Yes / No / N/A). These must
// render the literal word — NOT the green ✓/✗ checkbox used for true boolean
// columns — matching the legacy ALPACA list. Keyed by ModuleColumn.key.
// (user report 2026-08-26)
const FORCE_TEXT_COLS: ReadonlySet<string> = new Set(['defaultOpt']);

// ID columns render in the brand dark-orange (CSS `.mlv-id`, var --id-color)
// so the eye lands on the record key first.  Convention-driven so every
// ALPACA list form gets it for free: a column is an ID when its key ends in
// `id`/`Id`/`ID` (jobId, sampleId, reportId, oid, macId, …) OR its label is /
// ends with "ID"/"No"/"Number" (Job ID, MAC ID, Report No, User Number).
// Pure helper — same input, same output, no side effects.
const ID_KEY = /(^|[a-z])(id|oid)$/i;
const ID_LABEL = /\b(id|no|number)$/i;
export function isIdColumn(key: string, label: string): boolean {
  return ID_KEY.test(key) || ID_LABEL.test(label.trim());
}

interface Props { moduleKey: string; group: string; onHome: () => void; navTitle?: string; }

// ── MSR sample export (ALPACA parity 2026-07-10) ────────────────────────────
// Matches SelectAndExportMSRSamplesInxlsx_Execute / …InCsv_Execute in legacy.
// Fetches saved sample rows for every selected MSR Oid, combines them, and
// writes an XLSX or CSV with ALPACA's exact column headers.
// GoLive's exact 17-column export layout. The date header carries a TRAILING
// SPACE and the last column is SampleCollector — the client's program that
// consumes this file matches that layout, so both are reproduced verbatim
// (fix 2026-08-13: AI was exporting only 16 columns, no SampleCollector, which
// crashed the client program).
const MSR_SAMPLE_HEADERS = [
  'Run', 'SiteID', 'Address', 'KeyMap', 'Sample Collected Date & Time ',
  'FreeChlorine (mg/L)', 'TotalChlorine (mg/L)', 'MonoChloramine (mg/L Cl2)',
  'FreeAmmonia (mg/L NH3-N)', 'TotalColiform', 'E.coli',
  'Nitrite (mg/L)', 'Nitrate (mg/L)', 'SampleType', 'Disinfectant', 'Comments', 'SampleCollector',
];
// GoLive column widths — one entry per header (17).
const MSR_COL_WIDTHS = [6.29, 15.29, 34.43, 8.29, 28.29, 18.86, 19.29, 26, 26.14, 13.14, 11.86, 13.14, 13.57, 11.86, 11.71, 10.57, 16];

interface MsrSampleRow {
  projectId?: string; siteName?: string; address?: string; keyMap?: string;
  date?: string; time?: string;
  freeChlorine?: string; totalChlorine?: string; monochloramine?: string;
  freeAmmonia?: string; totalColiform?: string; ecoli?: string;
  nitrite?: string; nitrate?: string; sampleType?: string;
  disinfectant?: string; comment?: string; collector?: string;
}

// GoLive "Sample Collected Date & Time" format: MM/dd/yyyy hh:mm tt — zero-padded
// 12-hour + AM/PM (e.g. "08/04/2026 09:33 AM"). This is an EXTERNAL machine-read
// export consumed by the client's program, so it matches GoLive EXACTLY — NOT the
// app-wide 24-hour UI rule. AI was emitting "yyyy/MM/dd HH:mm", which the client
// program could not parse (fix 2026-08-13).
function msrFormatDateTime(date?: string, time?: string): string {
  if (!date) return '';
  const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  const datePart = d ? `${d[2]}/${d[3]}/${d[1]}` : date;
  const t = time ? /^(\d{1,2}):(\d{2})/.exec(time) : null;
  let timePart = '';
  if (t) {
    const h = parseInt(t[1] ?? '0', 10);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = (h % 12) === 0 ? 12 : h % 12;
    timePart = `${String(h12).padStart(2, '0')}:${t[2] ?? ''} ${ampm}`;
  }
  return [datePart, timePart].filter(Boolean).join(' ');
}

function msrSampleToRow(s: MsrSampleRow): string[] {
  return [
    s.projectId ?? '', s.siteName ?? '', s.address ?? '', s.keyMap ?? '',
    msrFormatDateTime(s.date, s.time),
    s.freeChlorine ?? '', s.totalChlorine ?? '', s.monochloramine ?? '',
    s.freeAmmonia ?? '', s.totalColiform ?? '', s.ecoli ?? '',
    s.nitrite ?? '', s.nitrate ?? '', s.sampleType ?? '', s.disinfectant ?? '', s.comment ?? '',
    s.collector ?? '',
  ];
}

function msrTimestamp(): string {
  const n = new Date();
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}${pad(n.getMonth() + 1)}${pad(n.getDate())}${pad(n.getHours())}${pad(n.getMinutes())}${pad(n.getSeconds())}`;
}

/**
 * MSR "Export To XLSX / CSV".
 *
 * Two PHASES, and they must not overlap: the file is fetched + built under the
 * BLOCKING loader, then the loader is released BEFORE the shared "File Ready to
 * Save" dialog opens. Wrapping the save in the loader put the blocking scrim on
 * top of the dialog, so "Save As" could not be clicked and the spinner hung
 * forever (fix 2026-08-14) — hence runLoading lives here, not at the call site.
 */
async function msrExportSamples(
  kind: 'xlsx' | 'csv',
  selected: Set<string>,
  notify: ReturnType<typeof import('../../components/notifications').useNotify>,
): Promise<void> {
  const filename = `MSRSamples_${msrTimestamp()}`;
  let built: { blob: Blob; name: string } | null = null;

  // ── Phase 1 — fetch the rows + build the file (blocking loader) ────────────
  try {
    built = await runLoading((async () => {
      const allRows: string[][] = [];
      for (const oid of [...selected]) {
        const resp = await api.get<{ success: boolean; data: MsrSampleRow[] }>(
          `/api/v1/monthly-summary-repository/${oid}/samples`);
        allRows.push(...(resp.data?.data ?? []).map(msrSampleToRow));
      }
      if (allRows.length === 0) return null;

      if (kind === 'xlsx') {
        // GoLive-matching XLSX: sheet "Data", bold header, font 11, freeze row 1 + col 1, exact column widths.
        const ExcelJS = (await import('exceljs')).default;
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Data');
        ws.addRow(MSR_SAMPLE_HEADERS);
        for (const row of allRows) ws.addRow(row);
        ws.eachRow((row) => {
          row.font = { size: 11 };
          row.eachCell((cell) => { cell.font = { size: 11 }; });
        });
        ws.getRow(1).eachCell((cell) => { cell.font = { bold: true, size: 11 }; });
        ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 1, topLeftCell: 'B2', activeCell: 'B2' }];
        MSR_COL_WIDTHS.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
        const buf = await wb.xlsx.writeBuffer();
        return {
          blob: new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          name: `${filename}.xlsx`,
        };
      }
      const csvCell = (s: string) => `"${String(s ?? '').replace(/"/g, '""')}"`;
      const csv = [MSR_SAMPLE_HEADERS, ...allRows].map((r) => r.map(csvCell).join(',')).join('\r\n');
      return {
        blob: new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }),
        name: `${filename}.csv`,
      };
    })(), { blocking: true, label: `Exporting to ${kind.toUpperCase()}…` });
  } catch {
    notify.error(`Export to ${kind.toUpperCase()} failed. Please try again.`);
    return;
  }

  if (!built) {
    notify.info('No sample data found for the selected MSR(s).');
    return;
  }

  // ── Phase 2 — ask WHERE to save (no loader on screen: dialog is clickable) ──
  try {
    if (!await saveBlobAs(built.blob, built.name)) return;   // cancelled → no toast
    notify.success(`Exported to ${kind.toUpperCase()} successfully.`);
  } catch {
    notify.error(`Export to ${kind.toUpperCase()} failed. Please try again.`);
  }
}
// ────────────────────────────────────────────────────────────────────────────

/** The single shared form layout — every ALPACA module renders through here
 *  so all forms are identical (same control order), with internal x+y scroll
 *  and responsive sizing. */
export function ModuleListView({ moduleKey, group, onHome, navTitle }: Props) {
  const notify = useNotify();
  const gateReason = useReasonGate();
  const { confirm: confirmDlg } = useConfirm();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // Default Date Range mirrors each GoLive list-view's own default Date Filter
  // (e.g. ClientCollectors ships with "3M").  Keyed on moduleKey; the parent
  // remounts this component per module so the lazy initializer re-runs.
  // `rangeDays` is the DRAFT (what the Date Range dropdown shows). On advanced
  // forms the query uses `appliedRange`, which is only committed when the user
  // clicks Retrieve (or OK in the panel) — so picking a range doesn't auto-fetch.
  const initRange = ENHANCED_TOOLBAR_MODULES.has(moduleKey)
    ? 90 // enhanced forms (Result Samples, Historic Results) default to 3M
    : (MODULE_DEFAULT_RANGE_DAYS[moduleKey] ?? 90); // ALPACA LIMS default is 3M (90d)
  const [rangeDays, setRangeDays] = useState<number | null>(() => initRange);
  const [appliedRange, setAppliedRange] = useState<number | null>(() => initRange);
  // Enhanced toolbar (orange dropdown, Retrieve-gated, colored buttons).
  const enhanced = ENHANCED_TOOLBAR_MODULES.has(moduleKey);
  // Full "Query Samples" dialog (Result Samples + Historic Results).
  const advanced = ADVANCED_FILTER_MODULES.has(moduleKey);
  // The combos + date ranges this module's Query Panel shows (only its own
  // grid columns).  Falls back to the full Result Samples layout.
  const queryFields = QUERY_FIELDS_BY_MODULE[moduleKey] ?? QUERY_FIELDS;
  const queryDates  = QUERY_DATES_BY_MODULE[moduleKey] ?? QUERY_DATES;
  // Balance the two-column panel: left = first N combos, right = the rest +
  // the date ranges (so each column holds ~half of all the rows).
  const queryLeftCount = Math.round((queryFields.length + queryDates.length) / 2);
  // Range actually used by the query: committed on enhanced forms, live elsewhere.
  const effectiveRange = enhanced ? appliedRange : rangeDays;
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Date Analyzed + Date Collected ranges (set by the "Query Samples" dialog).
  const [analysisFrom, setAnalysisFrom] = useState('');
  const [analysisTo, setAnalysisTo] = useState('');
  const [collectedFrom, setCollectedFrom] = useState('');
  const [collectedTo, setCollectedTo] = useState('');
  // "Query Samples" centered modal: open flag + draft date fields + draft
  // multi-select picks (per column) for the checkbox combos.
  const [queryOpen, setQueryOpen] = useState(false);
  const [qForm, setQForm] = useState<Record<string, string>>({});
  const [qSel, setQSel] = useState<Record<string, string[]>>({});
  // Data Package Review Checklist — the row whose "View Test List" link is open.
  const [linkedTests, setLinkedTests] = useState<{ label: string; tests: string } | null>(null);
  const [page, setPage] = useState(1);
  // Per-module initial page-size override (MODULE_CRUD.defaultPageSize) wins over
  // the global DEFAULT_PAGE_SIZE; falls back to the shared default otherwise.
  const [pageSize, setPageSize] = useState(
    MODULE_CRUD[moduleKey]?.defaultPageSize ?? DEFAULT_PAGE_SIZE);
  // Per-column multi-select filters (ALPACA-style dropdowns).
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  // Click-to-sort on a column header (server-side, whitelisted to column keys).
  // Lazy-init from the saved per-module grid prefs so the sort restores on return.
  const savedSort = useMemo(() => loadGridPrefs(moduleKey).sort, [moduleKey]);
  const [sortCol, setSortCol] = useState<string | null>(savedSort?.key ?? null);
  const [sortDesc, setSortDesc] = useState<boolean>(savedSort?.dir === 'desc');
  // Persist the sort whenever it changes (same store the column layout uses).
  useEffect(() => {
    saveGridPrefs(moduleKey, { sort: { key: sortCol, dir: sortCol ? (sortDesc ? 'desc' : 'asc') : null } });
  }, [moduleKey, sortCol, sortDesc]);
  const onSort = (key: string) => {
    if (sortCol === key) setSortDesc((d) => !d);
    else { setSortCol(key); setSortDesc(false); }
    setPage(1);
  };
  // Right-click header menu (Sort · Hide · Show All · Column Chooser) — adapt this
  // grid's own sort state to the shared <HeaderContextMenu>.  Clear Sort (key/dir
  // null) returns to the server's default order.  The menu is wired to the shared
  // column model (cm.columnMenu) LOWER DOWN — after `cols` (its source) is built —
  // so the hook call moved there; `headerMenu` is rendered once below (portals to body).
  const headerSort: HeaderMenuSort = {
    sort: { key: sortCol, dir: sortCol ? (sortDesc ? 'desc' : 'asc') : null },
    setSortExplicit: (key, dir) => {
      if (key === null || dir === null) { setSortCol(null); setSortDesc(false); }
      else { setSortCol(key); setSortDesc(dir === 'desc'); }
      setPage(1);
    },
  };

  // ── Opt-in CRUD (New / Edit / Delete / Audit) ───────────────────────────────
  // Only modules registered in MODULE_CRUD get write controls; every other
  // module renders exactly as before (read-only).  See moduleCrud.ts.
  // §2 — the BO class (boClass.ts) is the single source of truth. A Class E
  // (read-only) screen never gets a CRUD toolbar, even if MODULE_CRUD has an entry.
  // Every current CRUD module is A/B/C/F, so this changes nothing today; it's the
  // authoritative guardrail going forward (a dev warn flags any misclassification).
  const boClass = boClassOf(moduleKey);
  const crud = boClass === 'E' ? undefined : MODULE_CRUD[moduleKey];
  if (import.meta.env.DEV && MODULE_CRUD[moduleKey] && boClass === 'E') {
    // eslint-disable-next-line no-console
    console.warn(`[boClass] "${moduleKey}" defines MODULE_CRUD but is classified E (read-only) — classify it in layouts/boClass.ts.`);
  }
  // MAC action permissions (New=create, Edit=write, Delete=delete). Computed for
  // the active module but CONSUMED ONLY in the MSR toolbar below, so no other
  // module-CRUD form's buttons change (Monthly Summary Repository is the sole
  // reporting form that routes through ModuleListView). Falls open for admins /
  // when no MAC level applies — only tightens where MAC data exists.
  const perm = useNavActions(moduleKey);
  const queryClient = useQueryClient();
  // New/Edit now opens the shared full-page Settings Detail View (BO UI Standard
  // §4 / CLAUDE.md §18.6) instead of a popup: `detail` holds { id } while the
  // detail is front (id === null = New), null = the list is front.
  // (Monthly Summary Repository special-case: `detail` opens the bespoke
  // <MsrDetailDialog> overlay instead of the generic SettingsDetailPage.)
  const [detail, setDetail] = useState<{ id: string | null; mode?: 'view' | 'edit' } | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false); // MSR "Summary" dialog
  const [autoGenOpen, setAutoGenOpen] = useState(false); // MSR "AutoGenerate" dialog
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Prefix-invalidate every cached page of this module (queryKey ['module', key, params]).
  const refreshList = () => queryClient.invalidateQueries({ queryKey: ['module', moduleKey] });
  const openNew = () => setDetail({ id: null, mode: 'edit' });
  // Edit pencil / toolbar Edit → open the record EDITABLE.
  const openEdit = (row: Record<string, unknown>) => {
    if (!crud) return;
    setDetail({ id: String(row[crud.idField] ?? ''), mode: 'edit' });
  };
  // Row click → open the record READ-ONLY (view), matching the legacy XAF flow
  // (row → DetailView to see the data, with an Edit button to switch).
  const openView = (row: Record<string, unknown>) => {
    if (!crud) return;
    setDetail({ id: String(row[crud.idField] ?? ''), mode: 'view' });
  };
  // Toolbar "Edit" (canonical New · Edit · Delete) — edits the single checked
  // row via the same full-page detail; enabled only when exactly one is selected.
  const openEditSelected = () => {
    if (!crud || selected.size !== 1) return;
    setDetail({ id: Array.from(selected)[0]!, mode: 'edit' });
  };

  // ── Bulk load (Import + Template) — only for crud.bulkLoad lookups (Method) ──
  const importRef = useRef<HTMLInputElement>(null);
  // Template: a blank CSV of the non-readonly field labels (the same headers the
  // import endpoint binds).  Client-side — no backend needed.
  // NOTE: the Import/Template toolbar buttons are hidden for now
  // (user 2026-07-02) — the handlers stay wired for their return.
  const downloadTemplate = () => {
    if (!crud) return;
    const headers = crud.fields.filter((f) => f.type !== 'readonly').map((f) => f.label);
    const url = URL.createObjectURL(new Blob(['﻿' + headers.join(',') + '\r\n'],
      { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${crud.entityLabel}-import-template.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const pickImportFile = () => importRef.current?.click();
  // Keep the two handlers referenced while their toolbar buttons are hidden
  // (tsc noUnusedLocals) — remove these voids when the buttons return.
  void downloadTemplate; void pickImportFile;
  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-selected
    if (!file || !crud) return;
    const parsed = parseCsv(await file.text());
    if (parsed.length === 0) { notify.warning('No rows found in the file.'); return; }
    try {
      const res = await runLoading(
        api.post<{ data?: { created?: number; failed?: number } }>(`${crud.basePath}/import`, parsed),
        { blocking: true, label: `Importing ${parsed.length} row${parsed.length > 1 ? 's' : ''}…` });
      const created = res.data?.data?.created ?? 0;
      const failed = res.data?.data?.failed ?? 0;
      await refreshList();
      const noun = crud.entityLabel.toLowerCase();
      if (failed > 0) notify.warning(`Imported ${created} ${noun}${created === 1 ? '' : 's'}; ${failed} row${failed > 1 ? 's' : ''} skipped.`);
      else notify.success(`Imported ${created} ${noun}${created === 1 ? '' : 's'}.`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Import failed.';
      notify.error(msg);
    }
  };
  const toggleRow = (id: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else if (id) n.add(id);
    return n;
  });
  // Bulk delete the checked rows (ALPACA "Delete" toolbar action).
  const deleteSelected = async () => {
    if (!crud || selected.size === 0) return;
    const ids = Array.from(selected);
    // Shared centred danger confirm (standard dialog format, 2026-07-03 — was native window.confirm).
    const okDel = await confirmDlg({
      title: 'Delete', tone: 'danger', confirmLabel: 'Delete',
      message: `Delete ${ids.length} ${crud.entityLabel.toLowerCase()}${ids.length > 1 ? 's' : ''}? This cannot be undone.`,
    });
    if (!okDel) return;
    // Reason-for-change gate (standing rule 11) — prompts only when this module's
    // form has ReasonRequired ON; null = cancelled → abort.  runWithAuditReason
    // arms X-Audit-Reason so the generic delete's audit row carries the reason.
    const reason = await gateReason(crud.formName, 'delete');
    if (reason === null) return;
    setBulkDeleting(true);
    let failed = 0;
    await runLoading(runWithAuditReason(reason, async () => {
      for (const rid of ids) {
        try { await api.delete(`${crud.basePath}/${rid}`); } catch { failed++; }
      }
    }), { blocking: true, label: `Deleting ${ids.length} ${crud.entityLabel.toLowerCase()}${ids.length > 1 ? 's' : ''}…` });
    setBulkDeleting(false);
    setSelected(new Set());
    await refreshList();
    if (failed > 0) notify.error(`${failed} of ${ids.length} could not be deleted.`);
    else notify.success(`Deleted ${ids.length} ${crud.entityLabel.toLowerCase()}${ids.length > 1 ? 's' : ''}.`);
  };

  // ── UI-8: inline (in-cell) editing ───────────────────────────────────────
  // Editable text/textarea/date cells commit through the SAME update endpoint
  // the Edit dialog uses (GET the full record → merge the one changed field →
  // PUT), so the existing MediatR/EF update handler runs — audit
  // (IAuditTrailService) and UTC persistence happen server-side, never bypassed.
  // IDs, readonly, select and checkbox columns stay read-only here (edit them in
  // the dialog).
  const crudFieldByKey = useMemo(() => {
    const m: Record<string, CrudField> = {};
    if (crud) for (const f of crud.fields) m[f.key] = f;
    return m;
  }, [crud]);
  const inlineType = (key: string): 'text' | 'textarea' | 'date' | null => {
    // inlineRowEdit === false ⇒ no in-cell double-click editing (these simple
    // lists open the full detail on row-click / Edit instead). BO UI Standard.
    if (!crud || crud.inlineRowEdit === false || key === crud.idField) return null;
    const f = crudFieldByKey[key];
    if (!f) return null;
    return f.type === 'text' || f.type === 'textarea' || f.type === 'date' ? f.type : null;
  };
  const [editCell, setEditCell] = useState<{ id: string; key: string } | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const startEdit = (rowId: string, key: string, raw: unknown) => {
    if (!rowId) return;
    setEditDraft(inlineType(key) === 'date' ? toDateInputLocal(raw) : (raw == null ? '' : String(raw)));
    setEditCell({ id: rowId, key });
  };
  const cancelEdit = () => setEditCell(null);
  const commitEdit = async () => {
    if (!crud || !editCell) return;
    const f = crudFieldByKey[editCell.key];
    const draft = editDraft.trim();
    // Client-side validation (server FluentValidation stays authoritative).
    if (f?.required && draft === '') { notify.warning(`${f.label} is required.`); return; }
    if (f?.maxLength && draft.length > f.maxLength) {
      notify.warning(`${f.label} cannot exceed ${f.maxLength} characters.`); return;
    }
    // Reason-for-change gate (standing rule 11) — inline edit is an Update; prompt
    // only when this module's form has ReasonRequired ON. null = cancelled → abort.
    const reason = await gateReason(crud.formName, 'edit');
    if (reason === null) return;
    setSavingCell(true);
    try {
      await runWithAuditReason(reason, async () => {
      // Reuse the existing update path: pull the full current record, override
      // the one edited field, PUT it back through the audited handler.
      const res = await api.get<{ data?: Record<string, unknown> }>(`${crud.basePath}/${editCell.id}`);
      const rec = res.data?.data ?? {};
      const payload: Record<string, unknown> = {};
      for (const fld of crud.fields) {
        if (fld.type === 'readonly') continue;
        const source = fld.key === editCell.key ? editDraft : rec[fld.key];
        if (fld.type === 'checkbox') payload[fld.key] = Boolean(source);
        else if (fld.type === 'date') { const s = toDateInputLocal(source); payload[fld.key] = s === '' ? null : s; }
        else { const s = source == null ? '' : String(source).trim(); payload[fld.key] = s === '' ? null : s; }
      }
      await api.put(`${crud.basePath}/${editCell.id}`, payload);
      });
      setEditCell(null);
      await refreshList();
      notify.success('Change saved.');
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Save failed.';
      notify.error(msg);
    } finally {
      setSavingCell(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const setColFilter = (key: string, values: string[]) => {
    setFilters((f) => {
      const n = { ...f };
      if (values.length) n[key] = values; else delete n[key];
      return n;
    });
    setPage(1);
  };

  // Grouped forms load every row (no server paging) so the client can build
  // complete role groups + counts; the cap is the SP's 10k clamp.
  const groupBy = GROUPED_BY[moduleKey];
  // §9 fuzzy search for small Class A/B lookups (Method, Unit, QC Type, Holidays,
  // …): CRUD lookups that aren't enhanced / advanced / grouped. While the user is
  // typing, load the full set ONCE and fuzzy-filter + paginate in the browser so a
  // subsequence like "mbwkly" finds "Main Biweekly". Idle (empty box) keeps normal
  // server pagination, so a large lookup isn't fully loaded until it's searched.
  // Big server-paginated lists keep substring server search (a backend subsequence
  // param is a separate task).
  // §2/§9 — the class drives fuzzy eligibility: small Class A (lookup) / B (master
  // data) lists get client-side fuzzy; Class C/D/E/F keep server search.
  const clientFuzzy = (boClass === 'A' || boClass === 'B') && !enhanced && !advanced && !groupBy;
  const fuzzySearching = clientFuzzy && search.trim() !== '';
  const listParams = {
    page: fuzzySearching ? 1 : page,
    pageSize: fuzzySearching || groupBy ? 10000 : wirePageSize(pageSize),
    search: fuzzySearching ? '' : search,
    // Committed Date Range; a custom From/To (Date Received in the panel) overrides it.
    rangeDays: effectiveRange,
    filters, sort: sortCol, sortDesc,
    fromDate: advanced ? (fromDate || null) : null,
    toDate: advanced ? (toDate || null) : null,
    analysisFrom: advanced ? (analysisFrom || null) : null,
    analysisTo: advanced ? (analysisTo || null) : null,
    collectedFrom: advanced ? (collectedFrom || null) : null,
    collectedTo: advanced ? (collectedTo || null) : null,
  };
  const { data, isFetching, isError, error } = useModuleList(moduleKey, listParams);
  const mod = data?.module;
  // Preserve the column order exactly as the backend registry declares it.
  // ModuleRegistry hand-orders every module's columns to match the ALPACA
  // Go-Live layout, so we must NOT re-sort here (alphabetical sorting would
  // destroy that deliberate order).
  const cols = useMemo(() => {
    const base = mod?.columns ?? [];
    // MSR: un-pin the MSRID (first) column. A ~9-column grid needs no frozen
    // column, and the sticky-left offset (which ignores the leading checkbox)
    // misaligns the pinned cell — showing wrong once the Edit column was removed
    // (user request 2026-07-15). Other modules keep their frozen columns.
    return moduleKey === 'msr'
      ? base.map((c) => (c.frozen ? { ...c, frozen: false } : c))
      : base;
  }, [mod?.columns, moduleKey]);
  // Shared column model — Hide / Show-all / Column Chooser / drag-reorder /
  // resize — so this hand-rolled list behaves exactly like <AppDataGrid>. The
  // grid renders from cm.visibleColumns; the header menu gets the column items
  // via cm.columnMenu. (Created here, after its source `cols`.)
  // persistKey = moduleKey → each Data Center form restores its own column
  // visibility / order / widths on return (the component remounts per module).
  const cm = useColumnManager(cols, (c) => c.key, (c) => c.label, moduleKey);
  const { openHeaderMenu, headerMenu } = useHeaderMenu(headerSort, cm.columnMenu);
  // Default filter-list direction (CLAUDE.md §18.6, 2026-06-18): a column that
  // is NOT the active sort column still lists its filter values in the grid's
  // natural order — DESC when the first column is an ID (records newest-first),
  // else ASC.  The active sort column overrides this with its own direction.
  const filterDefaultDir: 'asc' | 'desc' = useMemo(() => {
    const first = cols[0];
    const isId = !!first && (/ID$/.test(first.label ?? '') || /Id$/.test(first.key ?? ''));
    return isId ? 'desc' : 'asc';
  }, [cols]);
  const rawRows = data?.data ?? [];
  const meta = data?.meta;
  // Client-side fuzzy (§9): filter + rank the full loaded set, then paginate in the
  // browser. No-op (rawRows unchanged) unless the user is actively fuzzy-searching a
  // client-fuzzy lookup. `search` ranks best-match first; empty query keeps DB order.
  const rowSearchText = (r: Record<string, unknown>) => cols.map((c) => fmt(r[c.key])).join(' ');
  const fuzzyFiltered = fuzzySearching ? fuzzyFilter(rawRows, search, rowSearchText) : rawRows;
  const rows = fuzzySearching
    ? fuzzyFiltered.slice((page - 1) * pageSize, page * pageSize)
    : rawRows;
  const totalPages = fuzzySearching
    ? Math.max(1, Math.ceil(fuzzyFiltered.length / pageSize))
    : Math.max(1, meta?.totalPages ?? 1);
  // One source for the header + footer count / page label across both modes.
  const displayCount = fuzzySearching ? fuzzyFiltered.length : (meta?.totalCount ?? 0);
  const displayPage = fuzzySearching ? page : (meta?.page ?? 1);
  const mapped = mod?.mapped ?? false;
  // CRUD selection helpers (depend on the current page's rows).  No-op when the
  // module isn't CRUD-enabled (crud === undefined).
  const pageIds = useMemo(
    () => (crud ? rows.map((r) => String((r as Record<string, unknown>)[crud.idField] ?? '')).filter(Boolean) : []),
    [crud, rows],
  );
  const pageAllSelected = !!crud && pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const toggleAll = (checked: boolean) => setSelected((s) => {
    const n = new Set(s);
    if (checked) pageIds.forEach((id) => n.add(id)); else pageIds.forEach((id) => n.delete(id));
    return n;
  });
  // First load of THIS module: the resolved data still belongs to the previous
  // module (keepPreviousData), so show only a loading spinner — never the
  // "data source mapping in progress" note — until this module's data arrives.
  const loadingThisModule = isFetching && mod?.key !== moduleKey;
  // Form title must match the sidebar/navigation name (navData label).
  const title = navTitle ?? mod?.title ?? moduleKey;

  // Frozen (sticky-left) columns — cumulative left offset from their widths.
  // Keyed by the RENDERED (visible, reordered) column index so frozen offsets stay
  // correct after a hide / reorder / resize.
  const frozenLeft = useMemo(() => {
    const map: Record<number, number> = {};
    let acc = 0;
    cm.visibleColumns.forEach((c, idx) => {
      if (c.frozen) { map[idx] = acc; acc += cm.colWidth(c.key) ?? c.width ?? 150; }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cm.visibleColumns, cm.colWidth]);
  const stickyStyle = (idx: number, header: boolean): CSSProperties | undefined => {
    if (!(idx in frozenLeft)) return undefined;
    return {
      position: 'sticky',
      left: frozenLeft[idx],
      zIndex: header ? 4 : 1,
    };
  };

  // ── Grouped view (e.g. User Role Right → grouped by Role, GoLive style) ──
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) =>
    setOpenGroups((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  // Detail columns = everything except the grouped column (it becomes the header).
  const groupCols = useMemo(() => cols.filter((c) => c.key !== groupBy?.col), [cols, groupBy]);
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const m = new Map<string, Record<string, unknown>[]>();
    for (const r of rows) {
      const k = String((r as Record<string, unknown>)[groupBy.col] ?? '');
      const arr = m.get(k);
      if (arr) arr.push(r as Record<string, unknown>);
      else m.set(k, [r as Record<string, unknown>]);
    }
    // A "placeholder" row (e.g. a user with no permissions at all) has every
    // detail column blank — keep the group header but drop the empty row so the
    // header reads "(Count=0)" with nothing underneath, matching GoLive.
    return Array.from(m, ([key, rs]) => ({
      key,
      rows: rs.filter((r) =>
        groupCols.some((c) => {
          const v = r[c.key];
          return v !== null && v !== undefined && String(v).trim() !== '';
        })),
    }));
  }, [groupBy, groupCols, rows]);

  // ── Report preview (CustomReports) — fetch the PDF through the authed
  // api client (Bearer header) and open it in the browser's PDF viewer. ──
  const [previewing, setPreviewing] = useState<string | null>(null);
  const openReport = async (reportId: string) => {
    if (!reportId || previewing) return;
    setPreviewing(reportId);
    const w = window.open('', '_blank'); // open synchronously so it's not popup-blocked
    try {
      const res = await api.get(`/api/v1/reports/${encodeURIComponent(reportId)}/pdf`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      if (w) { w.location.href = url; } else { window.open(url, '_blank'); }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      if (w) w.close();
      notify.error(`Report "${reportId}" could not be loaded.`);
    } finally {
      setPreviewing(null);
    }
  };

  // ── Export (shared full-format "Export to" — ALL 10 ALPACA formats) ──
  // This list is SERVER-paginated, so `rows` is only the visible page.  The
  // shared ExportMenu takes a rowsProvider that re-fetches EVERY filtered row
  // (huge pageSize) at export time, so "Export to" always exports the whole
  // filtered dataset, never just the current page (user request 2026-07-08).
  const exportMatrix = (list: readonly Record<string, unknown>[]) => ({
    headers: cols.map((c) => c.label),
    rows: list.map((r) => cols.map((c) => fmt(r[c.key]))),
  });
  const exportData: ExportData = {
    filename: moduleKey,
    ...exportMatrix(rows),
    rowsProvider: async () => {
      // Client-fuzzy modules: the full set is already loaded and fuzzy-filtered in
      // the browser, so export exactly what the user is seeing (ranked, filtered).
      if (fuzzySearching) return exportMatrix(fuzzyFiltered);
      // Fetch EVERY filtered row for the export. 10000 is the backend's hard
      // PageSize ceiling (GetModuleListQuery validator InclusiveBetween(1,10000));
      // a larger value 400s and breaks the export, so we request exactly the max
      // (covers every Data Center list). If a module ever exceeds 10000 filtered
      // rows the export takes the first 10000 in the current sort — flagged below.
      const resp = await fetchModuleRows(moduleKey, { ...listParams, page: 1, pageSize: 10000 });
      const all = resp.data ?? [];
      if ((resp.meta?.totalCount ?? all.length) > all.length) {
        notify.info(`Exporting the first ${all.length.toLocaleString()} of `
          + `${(resp.meta?.totalCount ?? 0).toLocaleString()} rows (export limit).`);
      }
      return exportMatrix(all);
    },
  };

  // ── "Query Samples" centered modal (ALPACA's Query button) ────────────────
  // Open the dialog, prefilling its multi-select picks from the applied column
  // filters and its date pairs from the applied ranges.
  const openQuery = () => {
    const sel: Record<string, string[]> = {};
    for (const f of queryFields) { const v = filters[f.key]; if (v?.length) sel[f.key] = [...v]; }
    // Monthly Summary Repository: default Month + Year to the CURRENT month/year
    // when not already filtered (ALPACA-LIMS parity — its Query opens pre-filled).
    // The Month column displays month NAMES, so seed the name.
    if (moduleKey === 'msr') {
      const nowQ = new Date();
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];
      const monthName = monthNames[nowQ.getMonth()] ?? 'January';
      if (!sel.year?.length)  sel.year  = [String(nowQ.getFullYear())];
      if (!sel.month?.length) sel.month = [monthName];
    }
    setQSel(sel);
    setQForm({
      collectedFrom, collectedTo,
      receivedFrom: fromDate, receivedTo: toDate,
      analyzedFrom: analysisFrom, analyzedTo: analysisTo,
    });
    setQueryOpen(true);
  };

  // Apply the dialog: multi-select picks → per-column IN-list filters; date pairs → ranges.
  const applyQuery = () => {
    setFilters((prev) => {
      const next = { ...prev };
      for (const f of queryFields) {
        const v = qSel[f.key];
        if (v?.length) next[f.key] = v;
        else delete next[f.key];
      }
      return next;
    });
    setCollectedFrom(qForm.collectedFrom ?? ''); setCollectedTo(qForm.collectedTo ?? '');
    setFromDate(qForm.receivedFrom ?? '');       setToDate(qForm.receivedTo ?? '');
    setAnalysisFrom(qForm.analyzedFrom ?? '');   setAnalysisTo(qForm.analyzedTo ?? '');
    setAppliedRange(rangeDays); // commit the (draft) Date Range too
    setQueryOpen(false);
    setPage(1);
  };

  // Refresh: commit the draft Date Range, reset to page 1, AND force a refetch
  // (invalidate the cached query) so it ALWAYS reloads — even when nothing
  // changed, a plain "Refresh" must re-pull from the server.
  const retrieve = () => { setAppliedRange(rangeDays); setPage(1); void refreshList(); };

  const activeQueryCount = queryFields.filter((f) => (filters[f.key]?.length ?? 0) > 0).length
    + (analysisFrom || analysisTo ? 1 : 0)
    + (collectedFrom || collectedTo ? 1 : 0);

  // ── Load timer + top progress bar (intervals cleaned up → no leak) ──
  const [liveMs, setLiveMs] = useState(0);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const startRef = useRef<number | null>(null);
  const prevFetch = useRef(false);
  useEffect(() => {
    if (isFetching && !prevFetch.current) { startRef.current = performance.now(); setLiveMs(0); }
    if (!isFetching && prevFetch.current && startRef.current != null) {
      setLoadMs(Math.round(performance.now() - startRef.current));
    }
    prevFetch.current = isFetching;
  }, [isFetching]);
  useEffect(() => {
    if (!isFetching) return;
    const id = window.setInterval(() => {
      if (startRef.current != null) setLiveMs(Math.round(performance.now() - startRef.current));
    }, 100);
    return () => window.clearInterval(id); // cleared on stop / unmount
  }, [isFetching]);

  const win = useMemo(() => {
    let s = Math.max(1, page - 2);
    const e = Math.min(totalPages, s + 4);
    s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  }, [page, totalPages]);

  // Date-range label for the non-advanced native <select> chrome (draft = applied there).
  const rangeKey = RANGES.find((r) => r.days === rangeDays)?.label ?? 'All';
  // Subtitle label reflects what's actually APPLIED: a custom Date Received range
  // (from the panel) wins, otherwise the committed preset label (e.g. "3M", "All").
  const appliedRangeKey = RANGES.find((r) => r.days === effectiveRange)?.label ?? 'All';
  const dateRangeLabel = (fromDate || toDate)
    ? `${fromDate || '…'} → ${toDate || '…'}`
    : (effectiveRange == null ? 'All dates' : appliedRangeKey);

  // ── Full-page Settings Detail View (New / Edit) — replaces the list while
  // front (BO UI Standard §4 / CLAUDE.md §18.6).  Same shared component for
  // every CRUD-enabled settings form, driven by that module's MODULE_CRUD
  // config.  Prev/next walks the current page's ids.
  // EXCEPTION — bespoke-detail modules (BESPOKE_DETAIL_MODULES): 'msr' (the
  // Year/Month/Category + Retrieve + samples grid) and 'msr-data-import-config'
  // (five multi-select GUID pickers + ImportMode radio) can't be expressed as a
  // MODULE_CRUD field list; they render as an overlay in the main return below
  // instead of replacing the list.
  // `crud.dialogEdit` forms opt OUT of the full-page detail entirely — they keep
  // the list front and render <ModuleCrudDialog> over it (see the list return).
  if (crud && detail && !BESPOKE_DETAIL_MODULES.has(moduleKey) && !crud.dialogEdit) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* List/Detail segmented switch — Detail active; List returns to the grid.
            Hidden for MSR: it moves its Load/Save/Cancel into the heading area. */}
        {moduleKey !== 'msr' && (
          <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
            <ViewToggle view="detail" onChange={(v) => { if (v === 'list') setDetail(null); }} />
          </div>
        )}
        {moduleKey === 'msr' ? (
          // MSR full-page inline detail (Retrieve/samples grid — can't be a
          // MODULE_CRUD field list) rendered Settings-style inside the tab.
          <MsrDetailDialog
            inline
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => { setDetail(null); void refreshList(); }}
            onClose={() => setDetail(null)}
            onNew={() => { setDetail(null); openNew(); }}
          />
        ) : moduleKey === 'set-samplefieldconfig' ? (
          // Sample Field Configuration — bespoke VisualMatrix field-config detail
          // (header + 3 tabs of available↔selected fields; §18.6 shared save flow).
          <SampleFieldConfigDetail
            id={detail.id}
            title={title}
            group={group}
            onHome={onHome}
            onSaved={() => { setDetail(null); void refreshList(); }}
            onClose={() => setDetail(null)}
          />
        ) : (
          <SettingsDetailPage
            config={crud}
            id={detail.id}
            initialMode={detail.mode}
            title={title}
            group={group}
            siblingIds={pageIds}
            onHome={onHome}
            onNavigate={(nid) => setDetail({ id: nid, mode: detail.mode })}
            onClose={() => setDetail(null)}
            onSaved={refreshList}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bl-page mlv-host">
      <div className={`mlv-progress${isFetching ? ' on' : ''}`} />
      {/* Hidden file picker for the bulk Import (crud.bulkLoad lookups). */}
      {crud?.bulkLoad && (
        <input ref={importRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
          onChange={onImportFile} />
      )}

      {/* ── Monthly Summary Repository — bespoke ALPACA-style dialogs ────────
          New/Edit (Year/Month/Category + Retrieve + samples grid), Summary
          (combined saved samples) and AutoGenerate (bulk headers per category).
          Merged team package 2026-07-02. */}
      {/* MSR (Monthly Summary Repository) New/Edit now renders FULL-PAGE inline
          in the List/Detail tab (see the early-return branch above) — no popup. */}
      {/* MSR Data Import Config — bespoke New/Edit dialog (System + five
          multi-select GUID pickers + ImportMode radio; follow-up slice to the
          2026-07-02 MSR merge). */}
      {/* Popup New/Edit — opt-in per form via MODULE_CRUD.dialogEdit (Settings ▸
          Permit Library New ▸ Permit Type New). The list stays front; this
          renders over it. Driven by the SAME MODULE_CRUD field list as the
          full-page detail, and saves through the shared useSaveFlow. */}
      {crud?.dialogEdit && detail && (
        <ModuleCrudDialog
          config={crud}
          id={detail.id}
          /* Row click opens 'view' (read-only, with an Edit button); the Edit
             pencil / toolbar Edit open 'edit'. New is always editable. */
          mode={detail.mode}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); void refreshList(); }}
        />
      )}
      {crud && detail && moduleKey === 'msr-data-import-config' && (
        <MsrDataImportConfigDialog
          id={detail.id}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); void refreshList(); }}
        />
      )}
      {moduleKey === 'msr' && summaryOpen && (
        <MsrSummaryDialog onClose={() => setSummaryOpen(false)} />
      )}
      {moduleKey === 'msr' && autoGenOpen && (
        <MsrAutoGenerateDialog
          onClose={() => setAutoGenOpen(false)}
          onDone={() => { setAutoGenOpen(false); void refreshList(); }}
        />
      )}

      {/* ── "Query Samples" centered modal (ALPACA parity) ───────────────────── */}
      {advanced && queryOpen && (
        <div className="qs-overlay" onMouseDown={() => setQueryOpen(false)}>
          <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="qs-titlebar">
              <span className="qs-title"><i className="ti ti-filter" /> Query Panel</span>
              <div className="qs-titlebar-right">
                {/* Preset Date Range (same as the toolbar — committed on OK) */}
                <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES} value={rangeDays} onChange={setRangeDays} />
                <i className="ti ti-x qs-close" title="Close" onClick={() => setQueryOpen(false)} />
              </div>
            </div>
            <div className="qs-body">
              {/* Two balanced columns: left = first N combos; right = the
                  remaining combos then this module's date ranges. */}
              <div className="qs-grid">
                <div className="qs-col">
                  {queryFields.slice(0, queryLeftCount).map((f) => (
                    <div className="qs-field" key={f.key}>
                      <label className="qs-label">{f.label}</label>
                      <QueryCombo moduleKey={moduleKey} column={f.key}
                        values={qSel[f.key] ?? []} placeholder={`Select ${f.label}…`}
                        cascade={qSel} descending={f.key === 'jobId'}
                        onChange={(v) => setQSel((s) => ({ ...s, [f.key]: v }))} />
                    </div>
                  ))}
                </div>
                <div className="qs-col">
                  {queryFields.slice(queryLeftCount).map((f) => (
                    <div className="qs-field" key={f.key}>
                      <label className="qs-label">{f.label}</label>
                      <QueryCombo moduleKey={moduleKey} column={f.key}
                        values={qSel[f.key] ?? []} placeholder={`Select ${f.label}…`}
                        cascade={qSel} descending={f.key === 'jobId'}
                        onChange={(v) => setQSel((s) => ({ ...s, [f.key]: v }))} />
                    </div>
                  ))}
                  {queryDates.map((d) => (
                    <div className="qs-field qs-field-date" key={d.key}>
                      <label className="qs-label">{d.label}</label>
                      <div className="qs-date-pair">
                        <QueryDatePicker ariaLabel={`${d.label} from`}
                          value={qForm[`${d.key}From`] ?? ''}
                          max={qForm[`${d.key}To`] || undefined}
                          onChange={(v) => setQForm((s) => ({ ...s, [`${d.key}From`]: v }))} />
                        <span className="qs-date-sep">–</span>
                        <QueryDatePicker ariaLabel={`${d.label} to`}
                          value={qForm[`${d.key}To`] ?? ''}
                          min={qForm[`${d.key}From`] || undefined}
                          onChange={(v) => setQForm((s) => ({ ...s, [`${d.key}To`]: v }))} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="qs-footer">
              <div className="qs-footer-actions">
                <button className="ca-btn ca-btn-solid" onClick={applyQuery}>
                  <span aria-hidden="true">⬇️</span> Load
                </button>
                <button className="ca-btn" onClick={() => { setQSel({}); setQForm({}); }}>
                  <i className="ti ti-filter-off" /> Clear all
                </button>
              </div>
              <span className="qs-footer-hint">Click <b>Load</b> to apply these filters.</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Fixed top: breadcrumb / title / subtitle (same as every other form) ─ */}
      <div className="bl-top">
        {/* List/Detail segmented switch (BO UI Standard) — top-LEFT, exactly
            where the prototype places it.  CRUD BOs only; Detail opens the
            selected row (or the first row) in the shared SettingsDetailPage. */}
        {/* List/Detail toggle hidden for MSR (buttons live in the heading instead). */}
        {crud && moduleKey !== 'msr' && (
          <div style={{ padding: '10px 0 0' }}>
            <ViewToggle view="list" detailDisabled={pageIds.length === 0}
              onChange={(v) => {
                if (v !== 'detail') return;
                const id = selected.size === 1 ? Array.from(selected)[0]! : pageIds[0];
                if (id) setDetail({ id });
              }} />
          </div>
        )}
        <div className="dp-page-hdr">
          {/* In-page breadcrumb REMOVED for CRUD modules (user 2026-07-02) — the
              topbar already shows the path.  EXCEPTION: MSR shows it, matching the
              Custom Reporting list standard (shared `dp-breadcrumb` → standard
              muted-link + ink-active colours). (user request 2026-07-15) */}
          {(!crud || moduleKey === 'msr') && (
            <div className="dp-breadcrumb">
              <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
              <i className="ti ti-chevron-right" />
              <span>{group}</span>
              <i className="ti ti-chevron-right" />
              <span className="dp-bc-active">{title}</span>
            </div>
          )}
          {/* ONE heading only (user 2026-07-02: "too many heading") — the top
              title takes the FormTitle style (nav icon tile + 16px/800 text +
              the load-time chip); the old second strip below is gone. */}
          <div className="dp-title-row">
            <FormTitle pageId={moduleKey} title={title}
              loadMs={isFetching ? liveMs : loadMs} loading={isFetching}
              auditFormName={crud?.formName} />
            {/* MSR "Load" — top-right of the title row (corner), matching the
                Custom Reporting list standard: opens the SELECTED record in its
                read-only detail view; enabled once exactly one row is ticked.
                (user request 2026-07-15) */}
            {moduleKey === 'msr' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  className="ca-btn ca-btn-solid"
                  onClick={openEditSelected}
                  disabled={selected.size !== 1}
                  title={selected.size === 1
                    ? 'Load the selected Monthly Summary'
                    : 'Tick exactly one row to enable Load.'}>
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
              </div>
            )}
          </div>
          <div className="dp-subtitle">
            {crud ? '' : 'Read-only · '}{displayCount.toLocaleString()} items
            {mod?.hasDateRange ? ` · ${enhanced ? dateRangeLabel : rangeKey}` : ''}
            {advanced && activeQueryCount > 0 ? ` · ${activeQueryCount} query filter${activeQueryCount > 1 ? 's' : ''}` : ''}
            {/* Descriptive tail (Custom Reporting subtitle style) — user request 2026-07-15. */}
            {moduleKey === 'msr' ? ' · Every monthly summary in the repository — list view' : ''}
            {!mapped && ' · data source mapping in progress'}
          </div>
        </div>
      </div>

      {/* ── Grid area — toolbar / search sit ON the grey page (EXACT prototype
          layout); only the GRID below is the white card. The FormTitle now
          lives in the page header above (one heading only). */}
      <div className="bl-grid-area">

          {/* MSR list guidance hint — shared <StepHint> (BO UI Standard §5),
              info (blue) tone, directly under the title and above the toolbar.
              (user request 2026-07-15) */}
          {moduleKey === 'msr' && (
            <StepHint tone="guidance">
              <b>Tick a report</b> to see what you can do with it — <b>Load</b> to view or edit it, or tick one or more to <b>Delete</b> them.
            </StepHint>
          )}

          {/* Toolbar on its OWN full-width row, left-aligned with Export pushed
              hard right (BO UI Standard §6 — the action bar is a separate row
              below the FormTitle, not squeezed onto the title row). */}
          <div className="mlv-toolbar-row">
            {/* Shared toolbar (CLAUDE.md §18.6) — canonical order:
                [left: Date-Range · Query] · New · Delete · Refresh · Export to.
                Audit is VIEW-ONLY and lives on the title row (FormTitle chip), not
                here (BO UI Standard v2, 2026-07-01).  Form-specific controls (the
                Date-Range preset and the "Query Samples" button) keep their existing
                order in the `left` slot; New/Delete are the standard CRUD props (only when this module
                is CRUD-enabled); the Export dropdown stays a self-anchored menu in
                the `right` slot so all three formats remain available. */}
            <AppToolbar
              left={<>
                {/* Preset Date Range — custom orange dropdown on enhanced forms
                    (selecting only stages the value; Refresh applies it). Other
                    forms keep the native auto-applying <select>. */}
                {/* §17 shared date-range chip row (1M·3M·6M·1Y·All, active = orange).
                    Enhanced forms also stage appliedRange; both apply immediately. */}
                {mod?.hasDateRange && enhanced && (
                  <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES} value={rangeDays}
                    onChange={(d) => { setRangeDays(d); setAppliedRange(d); setPage(1); }} />
                )}
                {mod?.hasDateRange && !enhanced && (
                  <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES} value={rangeDays}
                    onChange={(d) => { setRangeDays(d); setPage(1); }} />
                )}
                {/* MSR (user request 2026-07-09): standard New · Edit · Delete sit
                    RIGHT AFTER the Date Range and BEFORE the form-specific
                    Query / Summary / AutoGenerate.  Rendered inline here (and
                    suppressed on AppToolbar via onNew/onEdit/onDelete=undefined for
                    msr) because AppToolbar always draws its New/Edit/Delete AFTER
                    the whole left slot. */}
                {moduleKey === 'msr' && crud && (
                  <>
                    {/* Gated by the user's Module Access Control level for MSR
                        (MonthlySummaryRepository_ListView): New=Create, Edit=Write,
                        Delete=Delete. Hidden when the MAC doesn't grant them. */}
                    {perm.create && (
                      <button className="ca-btn" onClick={openNew} title="New">
                        <span className="tb-i tb-i-green">＋</span> New
                      </button>
                    )}
                    {perm.write && (
                      <button className="ca-btn" onClick={openEditSelected} disabled={selected.size !== 1} title="Edit">
                        <span className="tb-i tb-i-blue">✎</span> Edit
                      </button>
                    )}
                    {perm.delete && (
                      <button className="ca-btn" onClick={deleteSelected}
                        disabled={selected.size === 0 || bulkDeleting} title="Delete">
                        <span className="tb-i tb-i-red">🗑</span> Delete
                      </button>
                    )}
                  </>
                )}
                {/* "Query Samples" button — opens the centered modal */}
                {advanced && (
                  <button className="ca-btn rs-btn-query" onClick={openQuery}>
                    <i className="ti ti-filter" /> Query
                  </button>
                )}
                {/* MSR "Summary" — combined saved samples across MSRs (ALPACA parity) */}
                {moduleKey === 'msr' && (
                  <button className="ca-btn" onClick={() => setSummaryOpen(true)} title="Summary">
                    <i className="ti ti-table" /> Summary
                  </button>
                )}
                {/* MSR "AutoGenerate" — create headers for all categories (ALPACA parity) */}
                {moduleKey === 'msr' && (
                  <button className="ca-btn" onClick={() => setAutoGenOpen(true)} title="AutoGenerate">
                    <i className="ti ti-sparkles" /> AutoGenerate
                  </button>
                )}
                {/* MSR "Export To XLSX" / "Export To CSV" — standalone buttons matching
                    legacy ALPACA's SelectAndExportMSRSamplesInxlsx_Execute /
                    SelectAndExportMSRSamplesIncsv_Execute (ALPACA parity 2026-07-10).
                    Exports the SAMPLE-LEVEL rows (chemistry data) for the selected MSR(s),
                    NOT the MSR header grid — identical to the ALPACA LIMS behaviour.
                    Filename: MSRSamples_YYYYMMDDHHMMSS.xlsx / .csv */}
                {moduleKey === 'msr' && (
                  <>
                    <button className="ca-btn" title="Export To XLSX"
                      disabled={selected.size === 0}
                      onClick={() => void msrExportSamples('xlsx', selected, notify)}>
                      <i className="ti ti-file-type-xls" /> Export To XLSX
                    </button>
                    <button className="ca-btn" title="Export To CSV"
                      disabled={selected.size === 0}
                      onClick={() => void msrExportSamples('csv', selected, notify)}>
                      <i className="ti ti-file-type-csv" /> Export To CSV
                    </button>
                  </>
                )}
                {/* Refresh — coloured to match the Query/Export buttons on the
                    enhanced toolbar (the plain AppToolbar Refresh is used on
                    every other module).  Always force-reloads via `retrieve`. */}
                {enhanced && (
                  <button className="ca-btn rs-btn-refresh" onClick={retrieve}
                    disabled={isFetching} title="Refresh">
                    {isFetching ? <BusyIcon busy icon="ti-refresh" /> : <span className="tb-i tb-i-green">🔄</span>} Refresh
                  </button>
                )}
              </>}
              newLabel="New"
              // MSR renders New/Edit/Delete inline in the left slot (before
              // Query/Summary/AutoGenerate) — suppress the built-in ones so they
              // aren't drawn twice (user request 2026-07-09).
              // Sample Field Configuration is Edit-only (ALPACA parity: the list
              // hides New + Delete — a matrix's field config is edited, never
              // created/deleted from here).
              onNew={crud && moduleKey !== 'msr' && moduleKey !== 'set-samplefieldconfig' ? openNew : undefined}
              onEdit={crud && moduleKey !== 'msr' ? openEditSelected : undefined}
              editDisabled={selected.size !== 1}
              onDelete={crud && moduleKey !== 'msr' && moduleKey !== 'set-samplefieldconfig' ? deleteSelected : undefined}
              deleteDisabled={selected.size === 0 || bulkDeleting}
              onRefresh={enhanced ? undefined : retrieve}
              refreshing={isFetching}
              // Import + Template HIDDEN for now (user 2026-07-02) — bulk-load
              // wiring (pickImportFile / downloadTemplate) is kept intact;
              // restore by re-passing the two handlers.
              onImport={undefined}
              onTemplate={undefined}
              exportData={exportData}
            />
          </div>

          {/* Related-lookups family strip (BO UI Standard §4) — HIDDEN for now
              (user 2026-07-02: "set visible false … I will tell later").
              Re-enable by removing the `false &&`. */}
          {false && crud?.relatedLookups && (
            <RelatedLookups items={crud!.relatedLookups!} activeId={moduleKey}
              onSelect={(id) => {
                if (id !== moduleKey) notify.info('This form isn’t available yet.');
              }} />
          )}

          {/* Global search — OUTSIDE the grid card, on the grey page, styled
              exactly like the toolbar buttons (user 2026-07-02). */}
          <div className="list-search-row">
            <div className="list-search">
              {/* EXACT prototype .gsearch glyph (🔍 Search…). */}
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search…" value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)} />
              {searchInput && (
                <span className="list-search-clear" onClick={() => setSearchInput('')}>×</span>
              )}
            </div>
          </div>

        {/* Grid — the ONE white card on the grey page (EXACT prototype .grid). */}
        <div className="bl-card bl-card-flex">
          <div className="bl-tbl-wrap mlv-tbl-scroll">
            {loadingThisModule ? (
              // First load: show ONLY a loading indicator — no placeholder text.
              <div className="bl-empty" style={{ flexDirection: 'column', gap: 8 }}>
                <LoaderSymbol size={26} />
                <div style={{ color: '#666' }}>Loading…</div>
              </div>
            ) : !mapped ? (
              // Not yet data-mapped: still render the column headers so the grid
              // keeps its standard layout when there is no data, with a single
              // info row in place of the rows (mirrors GoLive's empty grids).
              <table className="bl-tbl mlv-table">
                <thead>
                  <tr>{cols.map((c, idx) => {
                    const sty = stickyStyle(idx, true);
                    return (
                      <th key={c.key} className={c.frozen ? 'mlv-frozen' : undefined}
                        style={{ ...(c.width ? { minWidth: c.width } : {}), ...(sty ?? {}) }}>
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label">{c.label}</span>
                        </div>
                      </th>
                    );
                  })}</tr>
                </thead>
                <tbody>
                  {!isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, cols.length)}>
                        No data to display — data source mapping in progress.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : isError ? (
              // 404 / UNKNOWN_MODULE = this nav item has no ModuleRegistry data
              // source (global ListView fallback) — show an empty list view, not
              // a hard error.  Any other failure is a real error.
              (() => {
                const e = error as { response?: { status?: number; data?: { error?: { code?: string } } } } | null;
                const notConfigured = e?.response?.status === 404 || e?.response?.data?.error?.code === 'UNKNOWN_MODULE';
                // A real load failure must NOT wipe the whole grid (columns +
                // headers) — if we already know the columns (from a prior load or
                // the cached module meta), keep the header row and show the error
                // as a single in-grid row, so a transient/filtered failure never
                // makes "all the data AND columns disappear" (user report
                // 2026-07-08).  Only the genuine "not configured" 404 stays a
                // full empty-state card.
                if (notConfigured) {
                  return (
                    <div className="bl-empty" style={{ color: '#64748b', flexDirection: 'column', gap: 6 }}>
                      <i className="ti ti-list-details" style={{ fontSize: 24, opacity: .35 }} />
                      <div>This form is not configured with a data source yet.</div>
                    </div>
                  );
                }
                if (cols.length === 0) {
                  return (
                    <div className="bl-empty" style={{ color: '#b91c1c', flexDirection: 'column', gap: 6 }}>
                      <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
                      <div>Failed to load data.</div>
                    </div>
                  );
                }
                return (
                  <table className="bl-tbl mlv-table">
                    <thead>
                      <tr>{cols.map((c, idx) => {
                        const sty = stickyStyle(idx, true);
                        return (
                          <th key={c.key} className={c.frozen ? 'mlv-frozen' : undefined}
                            style={{ ...(c.width ? { minWidth: c.width } : {}), ...(sty ?? {}) }}>
                            <div className="col-hdr-cell"><span className="col-hdr-label">{c.label}</span></div>
                          </th>
                        );
                      })}</tr>
                    </thead>
                    <tbody>
                      <tr className="mlv-nodata-row">
                        <td colSpan={Math.max(1, cols.length)} style={{ color: '#b91c1c' }}>
                          <i className="ti ti-alert-triangle" style={{ marginRight: 6, opacity: .6 }} />
                          Failed to load data — adjust the filter or click Refresh.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                );
              })()
            ) : groupBy && grouped ? (
              <table className="bl-tbl mlv-table mlv-grouped">
                <thead>
                  <tr>{groupCols.map((c) => (
                    <th key={c.key} style={c.width ? { minWidth: c.width } : undefined}>
                      <div className="col-hdr-cell"><span className="col-hdr-label">{c.label}</span></div>
                    </th>
                  ))}</tr>
                </thead>
                <tbody>
                  {grouped.map((g) => {
                    const open = openGroups.has(g.key);
                    return (
                      <Fragment key={g.key || '(blank)'}>
                        <tr className="mlv-group-row" onClick={() => toggleGroup(g.key)}>
                          <td colSpan={groupCols.length}>
                            <i className={`ti ti-${open ? 'square-minus' : 'square-plus'}`}
                              style={{ marginRight: 6, verticalAlign: 'middle' }} />
                            {groupBy.prefix}{g.key} (Count={g.rows.length.toLocaleString()})
                          </td>
                        </tr>
                        {open && g.rows.map((row, i) => (
                          <tr key={`${g.key}-${i}`}>
                            {groupCols.map((c) => {
                              const v = fmt(row[c.key]);
                              // Boolean permission columns render as the shared
                              // .cellcheck — GREEN filled ✓ when enabled (BO UI
                              // Standard prototype; user 2026-07-02).  Skipped for
                              // text-enum columns (FORCE_TEXT_COLS) so tri-state
                              // enums like Default (Yes/No/N/A) stay text.
                              if ((v === 'Yes' || v === 'No') && !FORCE_TEXT_COLS.has(c.key)) {
                                return (
                                  <td key={c.key} style={{ textAlign: 'center' }}>
                                    <span className={`cellcheck${v === 'Yes' ? ' on' : ''}`}
                                      role="img" aria-label={v === 'Yes' ? 'Enabled' : 'Disabled'} />
                                  </td>
                                );
                              }
                              if (v === 'Active' || v === 'Inactive') {
                                return (
                                  <td key={c.key}>
                                    <span className={`mlv-status${v === 'Active' ? ' on' : ''}`}>{v}</span>
                                  </td>
                                );
                              }
                              return <td key={c.key} title={v}>{v}</td>;
                            })}
                          </tr>
                        ))}
                      </Fragment>
                    );
                  })}
                  {grouped.length === 0 && !isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, groupCols.length)}>No data to display</td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="bl-tbl mlv-table">
                <thead>
                  <tr>{crud && (
                    <>
                      <th style={{ width: 40, textAlign: 'center' }}>
                        <input type="checkbox" aria-label="Select all on page"
                          checked={pageAllSelected}
                          onChange={(e) => toggleAll(e.target.checked)} />
                      </th>
                      {/* Per-row Edit pencil (ALPACA parity) — shown for every CRUD
                          module including MSR (opens the record directly via
                          openEdit). (MSR_Edit changeset 2026-07-15) */}
                      {/* Header caption intentionally BLANK (ALPACA parity — the
                          pencil column has no caption). aria-label keeps it
                          accessible (user request 2026-08-24). */}
                      <th style={{ width: 44, textAlign: 'center' }} aria-label="Edit">
                        <div className="col-hdr-cell"><span className="col-hdr-label" /></div>
                      </th>
                    </>
                  )}{cm.visibleColumns.map((c, idx) => {
                    const sty = stickyStyle(idx, true);
                    const w = cm.colWidth(c.key);
                    return (
                      <th key={c.key}
                        className={`${c.frozen ? 'mlv-frozen' : ''}${cm.dragClass(c.key)}`.trim() || undefined}
                        style={{ minWidth: w ?? c.width ?? undefined, ...(w ? { width: w } : {}), ...(sty ?? {}) }}
                        onContextMenu={(e) => openHeaderMenu(e, { field: c.key, label: c.label })}
                        {...cm.dragProps(c.key)}>
                        <div className="col-hdr-cell">
                          <span className="col-hdr-label mlv-sortable" onClick={() => onSort(c.key)}
                            title={`Sort by ${c.label}`}>
                            {c.label}
                          </span>
                          {/* UI-3: sort arrow at the column's right end (light grey, not bold).
                              Glyphs match the shared <SortableHeader>/<AppDataGrid> standard:
                              ⇅ unsorted · ▲ asc · ▼ desc (user request 2026-07-15). */}
                          <span className={`mlv-sort-ind${sortCol === c.key ? ' active' : ''}`}
                            onClick={() => onSort(c.key)}
                            aria-label={sortCol === c.key ? `sorted ${sortDesc ? 'desc' : 'asc'}` : 'sort'}>
                            {sortCol === c.key ? (sortDesc ? '▼' : '▲') : '⇅'}
                          </span>
                          {/* The Data Package Review Checklist Test column renders
                              "View Test List" links, not filterable values — hide
                              its column filter (user request 2026-08-26). */}
                          {!(moduleKey === 'data-package-checklist' && c.key === 'test') && (
                            <ColumnFilter
                              moduleKey={moduleKey}
                              column={c.key}
                              label={c.label}
                              rangeDays={mod?.hasDateRange ? effectiveRange : null}
                              selected={filters[c.key] ?? []}
                              cascade={filters}
                              sortDir={sortCol === c.key ? (sortDesc ? 'desc' : 'asc') : filterDefaultDir}
                              onChange={(v) => setColFilter(c.key, v)}
                            />
                          )}
                        </div>
                        {/* Resize handle (shared .adg-resize) — every column resizable. */}
                        <span className="adg-resize" title="Drag to resize"
                          onMouseDown={(e) => cm.startResize(c.key, e)}
                          onClick={(e) => e.stopPropagation()} />
                      </th>
                    );
                  })}</tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    // Stable key: CRUD grids key by the row id so checkbox/edit
                    // state stays bound to the right record under sort/filter
                    // (§18.6 — no array-index keys for interactive rows).
                    const rowKey = crud ? String(row[crud.idField] ?? i) : i;
                    return (
                    <tr key={rowKey}>
                      {crud && (
                        <>
                          <td style={{ textAlign: 'center', width: 40 }}>
                            <input type="checkbox" aria-label="Select row"
                              checked={selected.has(String(row[crud.idField] ?? ''))}
                              onChange={() => toggleRow(String(row[crud.idField] ?? ''))} />
                          </td>
                          {/* Per-row Edit pencil — every CRUD module incl. MSR. */}
                          <td style={{ textAlign: 'center', width: 44 }}>
                            <button title="Edit" onClick={() => openEdit(row)}
                              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#1F4E79', fontSize: 16, padding: '2px 4px' }}>
                              <i className="ti ti-edit" />
                            </button>
                          </td>
                        </>
                      )}
                      {cm.visibleColumns.map((c, idx) => {
                        const sty = stickyStyle(idx, false);
                        if (c.key === 'preview') {
                          const rid = String(row[c.key] ?? '');
                          return (
                            <td key={c.key} className={c.frozen ? 'mlv-frozen' : undefined} style={sty}>
                              {rid && (
                                <button className="mlv-preview-btn" title={`Preview ${rid}`}
                                  disabled={previewing === rid}
                                  onClick={() => openReport(rid)}>
                                  {previewing === rid ? '…' : <i className="ti ti-file-type-pdf" />}
                                </button>
                              )}
                            </td>
                          );
                        }
                        // Data Package Review Checklist: the Test column is a
                        // "View Test List" LINK opening the linked-tests popup
                        // (Test · Method), matching the legacy list view.
                        if (moduleKey === 'data-package-checklist' && c.key === 'test') {
                          const tests = String(row[c.key] ?? '').trim();
                          return (
                            <td key={c.key} className={c.frozen ? 'mlv-frozen' : undefined} style={sty}>
                              {tests ? (
                                <button className="mlv-link-btn mlv-link-blue" title="View Test List"
                                  onClick={() => setLinkedTests({ label: String(row['checkPoint'] ?? ''), tests })}>
                                  View Test List
                                </button>
                              ) : ''}
                            </td>
                          );
                        }
                        // Custom Reports: clicking the ReportID opens the report
                        // PDF (mirrors GoLive, where ReportID is the link).
                        if (c.key === 'reportId') {
                          const rid = String(row[c.key] ?? '');
                          return (
                            <td key={c.key} className={c.frozen ? 'mlv-frozen' : undefined} style={sty}>
                              {rid ? (
                                <button className="mlv-link-btn" title={`Open report ${rid}`}
                                  disabled={previewing === rid}
                                  onClick={() => openReport(rid)}>
                                  {previewing === rid ? '…' : rid}
                                </button>
                              ) : ''}
                            </td>
                          );
                        }
                        const val = fmt(row[c.key]);
                        // Boolean columns (Active, IsGroup, …) render as the
                        // shared .cellcheck — GREEN filled ✓ when enabled (BO UI
                        // Standard prototype; user 2026-07-02).  Skipped for
                        // text-enum columns (FORCE_TEXT_COLS) so tri-state enums like
                        // Default (Yes/No/N/A) render the literal word, matching legacy.
                        if ((val === 'Yes' || val === 'No') && !FORCE_TEXT_COLS.has(c.key)) {
                          return (
                            <td key={c.key}
                              className={c.frozen ? 'mlv-frozen' : undefined}
                              style={{ ...(sty ?? {}), textAlign: 'center' }}>
                              <span className={`cellcheck${val === 'Yes' ? ' on' : ''}`}
                                role="img" aria-label={val === 'Yes' ? 'Enabled' : 'Disabled'} />
                            </td>
                          );
                        }
                        // Lifecycle-status text (Active / Inactive) renders as coloured
                        // text — green for a live row, muted for a soft-deleted one.
                        // §18.6 keeps every other cell on the single ink token; a status
                        // value is the sanctioned exception (same rule as statusPill).
                        // The class MUST contain "status": theme/blackText.css forces
                        // `color: var(--ai-ink) !important` on every element inside a td
                        // except those matching [class*="status" i] / pill / chip / badge.
                        if (val === 'Active' || val === 'Inactive') {
                          return (
                            <td key={c.key}
                              className={c.frozen ? 'mlv-frozen' : undefined}
                              style={sty}>
                              <span className={`mlv-status${val === 'Active' ? ' on' : ''}`}>{val}</span>
                            </td>
                          );
                        }
                        const itype = inlineType(c.key);
                        const rid = crud ? String(row[crud.idField] ?? '') : '';
                        const editingThis = !!itype && !!editCell && editCell.id === rid && editCell.key === c.key;
                        const tdCls = [
                          c.frozen ? 'mlv-frozen' : '',
                          isIdColumn(c.key, c.label) ? 'mlv-id' : '',
                          itype ? 'mlv-editable' : '',
                        ].filter(Boolean).join(' ') || undefined;
                        if (editingThis) {
                          // UI-8: in-cell editor — commits on Enter/blur, cancels on Escape.
                          return (
                            <td key={c.key} className={tdCls} style={sty}>
                              {itype === 'textarea' ? (
                                <textarea autoFocus className="mlv-inline-edit" rows={2}
                                  value={editDraft} disabled={savingCell}
                                  maxLength={crudFieldByKey[c.key]?.maxLength}
                                  onChange={(e) => setEditDraft(e.target.value)}
                                  onBlur={() => { void commitEdit(); }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void commitEdit(); }
                                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                  }} />
                              ) : (
                                <input autoFocus className="mlv-inline-edit"
                                  type={itype === 'date' ? 'date' : 'text'}
                                  value={editDraft} disabled={savingCell}
                                  maxLength={crudFieldByKey[c.key]?.maxLength}
                                  onChange={(e) => setEditDraft(e.target.value)}
                                  onBlur={() => { void commitEdit(); }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); void commitEdit(); }
                                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                  }} />
                              )}
                            </td>
                          );
                        }
                        // Row-click opens the record READ-ONLY (view) — only for
                        // non-inline CRUD forms, so inline double-click-to-edit
                        // modules are unaffected. The Edit pencil opens editable.
                        const rowClickView = crud?.inlineRowEdit === false;
                        return <td key={c.key} title={itype ? 'Double-click to edit' : val}
                          className={tdCls}
                          style={rowClickView ? { ...(sty ?? {}), cursor: 'pointer' } : sty}
                          onClick={rowClickView ? () => openView(row) : undefined}
                          onDoubleClick={itype ? () => startEdit(rid, c.key, row[c.key]) : undefined}>{val}</td>;
                      })}
                    </tr>
                    );
                  })}
                  {rows.length === 0 && !isFetching && (
                    <tr className="mlv-nodata-row">
                      <td colSpan={Math.max(1, cols.length + (crud ? 2 : 0))}>No data to display</td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            {isFetching && !loadingThisModule && <div className="bl-loading"><LoaderSymbol size={16} /> Loading…</div>}
            {/* Header right-click menu (Sort · Hide · Show All · Column Chooser) +
                the Column Chooser popup — both portal to <body>; rendered once. */}
            {headerMenu}
            {cm.chooser}
          </div>

          {/* Pagination — same chrome as every other form */}
          {mapped && !groupBy && (
            <div className="bl-pgbar">
              <span className="pgb-info">
                Page {displayPage} of {totalPages} ({displayCount.toLocaleString()} items)
              </span>
              <div className="pgb-nav">
                <button className="pgb-btn" disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <i className="ti ti-chevron-left" />
                </button>
                {win[0]! > 1 && <button className="pgb-btn" onClick={() => setPage(1)}>1</button>}
                {win[0]! > 2 && <span className="pgb-ellipsis">…</span>}
                {win.map((n) => (
                  <button key={n} className={`pgb-btn${n === page ? ' pgb-active' : ''}`}
                    onClick={() => setPage(n)}>{n}</button>
                ))}
                {win[win.length - 1]! < totalPages && (
                  <button className="pgb-btn" onClick={() => setPage(totalPages)}>{totalPages}</button>
                )}
                <button className="pgb-btn" disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  <i className="ti ti-chevron-right" />
                </button>
              </div>
              <div className="pgb-size">
                <span className="pgb-size-lbl">Page size:</span>
                <select className="pgb-size-sel" value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{pageSizeLabel(s)}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Data Package Review Checklist — "View Test List" popup (Test · Method). */}
      {linkedTests && (
        <LinkedTestsModal tests={linkedTests.tests}
          onClose={() => setLinkedTests(null)} />
      )}
    </div>
  );
}

/** Linked-tests popup for the Data Package Review Checklist list view — the
 *  legacy "View Test List" dialog: a Test · Method grid with search + paging.
 *  Parses the row's ALREADY-LOADED `tests` string ("TestName|Method;…") in
 *  memory — no API call, so the data shows instantly with no loading flash. */
function LinkedTestsModal({ tests, onClose }: {
  tests: string; onClose: () => void;
}) {
  const rows = useMemo(() => tests.split(';')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const i = p.indexOf('|');
      return i >= 0
        ? { test: p.slice(0, i).trim(), method: p.slice(i + 1).trim() }
        : { test: p, method: '' };
    }), [tests]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const term = q.trim().toLowerCase();
  const filtered = term
    ? rows.filter((r) => `${r.test} ${r.method ?? ''}`.toLowerCase().includes(term))
    : rows;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="ltm-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="ltm-box" onClick={(e) => e.stopPropagation()}>
        <div className="ltm-head">
          <span>Linked Tests</span>
          <button className="ltm-x" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="ltm-search">
          <input placeholder="Search…" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        <div className="ltm-grid">
          <table>
            <thead><tr><th>Test</th><th>Method</th></tr></thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr><td colSpan={2} className="ltm-empty">No data to display</td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={i}><td>{r.test}</td><td>{r.method ?? ''}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ltm-foot">
          <span>Page {page} of {totalPages} ({filtered.length} items)</span>
          <span className="ltm-pager">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button key={n} className={n === page ? 'ltm-pg-active' : ''}
                onClick={() => setPage(n)}>{n}</button>
            ))}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>›</button>
          </span>
          <span className="ltm-size">
            Page size:
            <select value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[10, 20, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </span>
        </div>
      </div>
    </div>
  );
}
